import React from 'react';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { IconCard, IconEmbed, IconInline } from '@atlaskit/editor-common/card';
import commonMessages, { cardMessages, mediaAndEmbedToolbarMessages } from '@atlaskit/editor-common/messages';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import DownloadIcon from '@atlaskit/icon/glyph/download';
import RemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import { messages } from '@atlaskit/media-ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { showLinkingToolbar } from '../commands/linking';
import { getMediaLinkingState } from '../pm-plugins/linking';
import ImageBorderItem from '../ui/ImageBorder';
import { currentMediaOrInlineNodeBorderMark } from '../utils/current-media-node';
import { isImage } from '../utils/is-type';
import { altTextButton } from './alt-text';
import { changeInlineToMediaCard, changeMediaInlineToMediaSingle, removeInlineCard, setBorderMark, toggleBorderMark } from './commands';
import { FilePreviewItem } from './filePreviewItem';
import { shouldShowImageBorder } from './imageBorder';
import { LinkToolbarAppearance } from './linking-toolbar-appearance';
import { downloadMedia } from './utils';
import { generateFilePreviewItem } from './index';
export var generateMediaInlineFloatingToolbar = function generateMediaInlineFloatingToolbar(state, intl, mediaPluginState, hoverDecoration, pluginInjectionApi) {
  var _pluginInjectionApi$a, _pluginInjectionApi$f;
  var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
  var editorAnalyticsAPI = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a === void 0 ? void 0 : _pluginInjectionApi$a.actions;
  var forceFocusSelector = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$f = pluginInjectionApi.floatingToolbar) === null || _pluginInjectionApi$f === void 0 || (_pluginInjectionApi$f = _pluginInjectionApi$f.actions) === null || _pluginInjectionApi$f === void 0 ? void 0 : _pluginInjectionApi$f.forceFocusSelector;
  var mediaInline = state.schema.nodes.mediaInline;
  var mediaType = state.selection instanceof NodeSelection && state.selection.node.attrs.type;
  var mediaLinkingState = getMediaLinkingState(state);
  if (mediaPluginState.allowInlineImages && isImage(mediaType)) {
    return getMediaInlineImageToolbar(state, intl, mediaPluginState, hoverDecoration, editorAnalyticsAPI, pluginInjectionApi, mediaLinkingState, options);
  }
  var items = [{
    id: 'editor.media.view.switcher.inline',
    type: 'button',
    icon: IconInline,
    selected: true,
    disabled: false,
    focusEditoronEnter: true,
    onClick: function onClick() {
      return true;
    },
    title: intl.formatMessage(cardMessages.inlineTitle),
    testId: 'inline-appearance',
    className: 'inline-appearance' // a11y. uses to force focus on item
  }, {
    id: 'editor.media.view.switcher.thumbnail',
    type: 'button',
    icon: IconCard,
    selected: false,
    disabled: false,
    focusEditoronEnter: true,
    onClick: changeInlineToMediaCard(editorAnalyticsAPI, forceFocusSelector),
    title: intl.formatMessage(cardMessages.blockTitle),
    testId: 'thumbnail-appearance',
    className: 'thumbnail-appearance' // a11y. uses to force focus on item
  }, {
    type: 'separator'
  }, {
    type: 'custom',
    fallback: [],
    render: function render() {
      return /*#__PURE__*/React.createElement(FilePreviewItem, {
        key: "editor.media.card.preview",
        mediaPluginState: mediaPluginState,
        intl: intl
      });
    }
  }, {
    type: 'separator'
  }, {
    id: 'editor.media.card.download',
    type: 'button',
    icon: DownloadIcon,
    onClick: function onClick() {
      downloadMedia(mediaPluginState);
      return true;
    },
    title: intl.formatMessage(messages.download)
  }, {
    type: 'separator'
  }, {
    type: 'copy-button',
    supportsViewMode: true,
    items: [{
      state: state,
      formatMessage: intl.formatMessage,
      nodeType: mediaInline
    }]
  }, {
    type: 'separator'
  }, {
    id: 'editor.media.delete',
    type: 'button',
    appearance: 'danger',
    focusEditoronEnter: true,
    icon: RemoveIcon,
    onMouseEnter: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, true),
    onMouseLeave: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, false),
    onFocus: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, true),
    onBlur: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, false),
    title: intl.formatMessage(commonMessages.remove),
    onClick: removeInlineCard,
    testId: 'media-toolbar-remove-button'
  }];
  return items;
};
var getMediaInlineImageToolbar = function getMediaInlineImageToolbar(state, intl, mediaPluginState, hoverDecoration, editorAnalyticsAPI, pluginInjectionApi, mediaLinkingState) {
  var _pluginInjectionApi$w, _pluginInjectionApi$f2;
  var options = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : {};
  var mediaInline = state.schema.nodes.mediaInline;
  var mediaInlineImageTitle = intl.formatMessage(mediaAndEmbedToolbarMessages.changeToMediaInlineImage);
  var mediaSingleTitle = intl.formatMessage(mediaAndEmbedToolbarMessages.changeToMediaSingle);
  var widthPluginState = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$w = pluginInjectionApi.width) === null || _pluginInjectionApi$w === void 0 ? void 0 : _pluginInjectionApi$w.sharedState.currentState();
  var inlineImageItems = [];
  var isNestingInQuoteSupported = (pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$f2 = pluginInjectionApi.featureFlags) === null || _pluginInjectionApi$f2 === void 0 || (_pluginInjectionApi$f2 = _pluginInjectionApi$f2.sharedState.currentState()) === null || _pluginInjectionApi$f2 === void 0 ? void 0 : _pluginInjectionApi$f2.nestMediaAndCodeblockInQuote) || fg('editor_nest_media_and_codeblock_in_quotes_jira');
  if (shouldShowImageBorder(state)) {
    inlineImageItems.push({
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        if (!editorView) {
          return null;
        }
        var dispatch = editorView.dispatch,
          state = editorView.state;
        var borderMark = currentMediaOrInlineNodeBorderMark(state);
        return /*#__PURE__*/React.createElement(ImageBorderItem, {
          toggleBorder: function toggleBorder() {
            var _pluginInjectionApi$a2;
            toggleBorderMark(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a2 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a2 === void 0 ? void 0 : _pluginInjectionApi$a2.actions)(state, dispatch);
          },
          setBorder: function setBorder(attrs) {
            var _pluginInjectionApi$a3;
            setBorderMark(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a3 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a3 === void 0 ? void 0 : _pluginInjectionApi$a3.actions)(attrs)(state, dispatch);
          },
          borderMark: borderMark,
          intl: intl
        });
      }
    });
    inlineImageItems.push({
      type: 'separator'
    });
  }
  inlineImageItems.push({
    id: 'editor.media.convert.mediainline',
    type: 'button',
    title: mediaInlineImageTitle,
    icon: function icon() {
      return /*#__PURE__*/React.createElement(IconInline, {
        size: "medium",
        label: mediaInlineImageTitle
      });
    },
    onClick: function onClick() {
      return true;
    },
    selected: true
  }, {
    id: 'editor.media.convert.mediasingle',
    type: 'button',
    title: mediaSingleTitle,
    icon: function icon() {
      return /*#__PURE__*/React.createElement(IconEmbed, {
        size: "medium",
        label: mediaSingleTitle
      });
    },
    onClick: changeMediaInlineToMediaSingle(editorAnalyticsAPI, widthPluginState, isNestingInQuoteSupported)
  }, {
    type: 'separator'
  });
  inlineImageItems.push({
    type: 'custom',
    fallback: [],
    render: function render(editorView, idx) {
      if (editorView !== null && editorView !== void 0 && editorView.state) {
        var editLink = function editLink() {
          if (editorView) {
            var _state = editorView.state,
              dispatch = editorView.dispatch;
            showLinkingToolbar(_state, dispatch);
          }
        };
        var openLink = function openLink() {
          if (editorView) {
            var _pluginInjectionApi$a4;
            var tr = editorView.state.tr,
              dispatch = editorView.dispatch;
            pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a4 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a4 === void 0 || _pluginInjectionApi$a4.actions.attachAnalyticsEvent({
              eventType: EVENT_TYPE.TRACK,
              action: ACTION.VISITED,
              actionSubject: ACTION_SUBJECT.MEDIA,
              actionSubjectId: ACTION_SUBJECT_ID.LINK
            })(tr);
            dispatch(tr);
            return true;
          }
        };
        return /*#__PURE__*/React.createElement(LinkToolbarAppearance, {
          key: idx,
          editorState: editorView.state,
          intl: intl,
          mediaLinkingState: mediaLinkingState,
          onAddLink: editLink,
          onEditLink: editLink,
          onOpenLink: openLink,
          isInlineNode: true,
          isViewOnly: options.isViewOnly
        });
      }
      return null;
    },
    supportsViewMode: true
  });

  //Image Preview
  if (options.allowImagePreview) {
    inlineImageItems.push(generateFilePreviewItem(mediaPluginState, intl), {
      type: 'separator',
      supportsViewMode: true
    });
  }
  if (options.isViewOnly) {
    inlineImageItems.push({
      id: 'editor.media.image.download',
      type: 'button',
      icon: DownloadIcon,
      onClick: function onClick() {
        downloadMedia(mediaPluginState, options.isViewOnly);
        return true;
      },
      title: intl.formatMessage(messages.download),
      supportsViewMode: true
    }, {
      type: 'separator',
      supportsViewMode: true
    });
  }
  if (options.allowAltTextOnImages) {
    var _pluginInjectionApi$a5;
    inlineImageItems.push(altTextButton(intl, state, pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a5 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a5 === void 0 ? void 0 : _pluginInjectionApi$a5.actions), {
      type: 'separator'
    });
  }
  inlineImageItems.push({
    type: 'copy-button',
    supportsViewMode: true,
    items: [{
      state: state,
      formatMessage: intl.formatMessage,
      nodeType: mediaInline
    }]
  });
  inlineImageItems.push({
    type: 'separator'
  });
  inlineImageItems.push({
    id: 'editor.media.delete',
    type: 'button',
    appearance: 'danger',
    focusEditoronEnter: true,
    icon: RemoveIcon,
    onMouseEnter: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, true),
    onMouseLeave: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, false),
    onFocus: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, true),
    onBlur: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(mediaInline, false),
    title: intl.formatMessage(commonMessages.remove),
    onClick: removeInlineCard,
    testId: 'media-toolbar-remove-button'
  });
  return inlineImageItems;
};